import {
  ScrollableWithBackToTop,
  ScrollableWithBackToTopHandle,
} from '@app/core/ui/ScrollableWithBackToTop';
import { nlpIntegration } from '@mi-front/api-clients';
import { Box, Typography } from '@mui/material';
import { RefObject } from 'react';

import { SCROLL_TOP_THRESHOLD_PX } from '../consts';
import { isTopicActiveAt, isTopicWithinLast15Min } from '../live-topics-panel-utils';
import { LiveTopicsListSkeleton } from './LiveTopicsListSkeleton';
import { LiveTopicsNewTopicsPill } from './LiveTopicsNewTopicsPill';
import { LiveTopicsPanelItem } from './LiveTopicsPanelItem';

export interface LiveTopicsPanelListViewProps {
  scrollableRef: RefObject<ScrollableWithBackToTopHandle | null>;
  onScrollToTop: () => void;
  isLoading: boolean;
  filteredTopics: nlpIntegration.TopicOutDto[];
  emptyMessage: string;
  newlyArrivedTopicIds: Set<string>;
  viewedInDetailTopicIds: Set<string>;
  currentTs: number | null;
  onTopicClick: (topicId: string) => void;
  newTopicsPillLabel: string;
  newTopicsCount: number;
}

/**
 * Scrollable list of live topics with skeleton/empty states and the "N temas nuevos" pill.
 */
export function LiveTopicsPanelListView({
  scrollableRef,
  onScrollToTop,
  isLoading,
  filteredTopics,
  emptyMessage,
  newlyArrivedTopicIds,
  viewedInDetailTopicIds,
  currentTs,
  onTopicClick,
  newTopicsPillLabel,
  newTopicsCount,
}: LiveTopicsPanelListViewProps) {
  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <ScrollableWithBackToTop
        ref={scrollableRef}
        onScrollToTop={onScrollToTop}
        scrollTopThresholdPx={SCROLL_TOP_THRESHOLD_PX}
      >
        {isLoading ? (
          <LiveTopicsListSkeleton />
        ) : filteredTopics.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          filteredTopics.map((topic) => (
            <LiveTopicsPanelItem
              key={topic.id}
              topic={topic}
              onClick={() => onTopicClick(topic.id)}
              showNewChip={
                newlyArrivedTopicIds.has(topic.id) &&
                !viewedInDetailTopicIds.has(topic.id) &&
                isTopicWithinLast15Min(topic)
              }
              isActive={isTopicActiveAt(topic, currentTs)}
              isNew={newlyArrivedTopicIds.has(topic.id)}
            />
          ))
        )}
      </ScrollableWithBackToTop>
      <LiveTopicsNewTopicsPill
        label={newTopicsPillLabel}
        count={newTopicsCount}
        onClick={() => scrollableRef.current?.scrollToTop()}
      />
    </Box>
  );
}
